/* eslint-disable @typescript-eslint/no-explicit-any */

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	Text,
	Box,
	Image,
	rem,
	Group,
	Container,
	Tooltip,
} from '@mantine/core';
import clsx from 'clsx';

// I18n
import { useTranslation } from 'react-i18next';

// UI lib components
import { Button } from '@components/atoms/Button';
import { RingProgressIndicator } from './RingProgressIndicator';

// Icons
import CalendarIcon from '@assets/icons/calendar.svg';

// Styles
import classes from './RowMintHistory.module.css';

// Utils
import { getMintHistoryStatus } from '@utils/mint-status';

/* -------------------------------------------------------------------------- */
/*                                    Types                                   */
/* -------------------------------------------------------------------------- */
type TMintHistoryData = {
	status: string;
	progress: string;
	project: string;
	mintDate: string;
	nbrOfProductions: number;
	tCO2eqProduct: number;
	mint: { status: string };
	pin: { status: string };
	destroy?: {
		updatedAt: string;
		by: string;
		reason: string;
		status: string;
		transactionHash: string;
	};
};

type TRowMintHistoryProps = {
	verified: boolean;
	data: TMintHistoryData;
	handleviewdetails: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const RowMintHistory = (
	props: TRowMintHistoryProps
) => {
	/* ******************************** HOOKS ******************************* */
	const { t } = useTranslation();

	/* ******************************** RENDERING ******************************* */
	return (
		<Container className={classes.row}>
			<Box
				className={clsx(classes.col, classes.colProject)}
			>
				<Text className={classes.title}>
					{t('global.project')}
				</Text>
				<Text className={classes.value}>
					{props.data.project}
				</Text>
			</Box>
			<Box className={classes.col}>
				<Group gap={'5px'}>
					<Image
						style={{ width: rem(18), height: rem(18) }}
						src={CalendarIcon}
					/>
					<Text className={classes.title}>
						{t('global.mintDate')}
					</Text>
				</Group>
				<Text className={classes.value}>
					{props.data.mintDate}
				</Text>
			</Box>
			<Box className={classes.col}>
				<Text className={classes.title}>
					{t('global.nbrOfProductions')}
				</Text>
				<Text className={classes.value}>
					{props.data.nbrOfProductions}
				</Text>
			</Box>
			<Box className={classes.colTco2eqProduced}>
				<Text className={classes.title}>
					{t('global.TCO2eqProduced')}
				</Text>
				<Text className={classes.value}>
					{props.data.tCO2eqProduct}
				</Text>
			</Box>
			<Box className={classes.colRingProgess}>
				<Tooltip
					label={
						props.data?.destroy &&
						props.data.destroy?.status == 'SUCCESS'
							? props.data.destroy?.reason || 'DESTROYED'
							: t(
									getMintHistoryStatus(
										props.data.mint.status,
										props.data.pin.status,
										props.data.status,
										props.data.progress
									)
								)
					}
				>
					<Box>
						<RingProgressIndicator
							verified={props.verified}
							progress={props.data.progress}
							status={props.data.status}
							destroy={props.data?.destroy}
						/>
					</Box>
				</Tooltip>
			</Box>
			<Box className={classes.col}>
				<Box className={classes.containerButton}>
					<Button
						onClick={props.handleviewdetails}
						fullWidth
						variant="outline"
						size="sm"
					>
						{t('global.viewDetails')}
					</Button>
				</Box>
			</Box>
		</Container>
	);
};
